<script setup lang="ts">
import { onUpdated, ref, watch } from 'vue';
import type { SlimOrganization } from '@ilteducation/my-account-types';
import useModal, { ModalTypes } from '../../composables/useModal';
import Modal from '../Modal.vue';
import { generateQrToken, generateRandomPassword } from '../../utils';
import { useLocale } from '../../composables/useLocale';
import { UserToCreate } from './useCreateMultipleUsers';
import { Role } from '../../types/user';
import { useUserRoles } from '../../composables/useUserRoles';

const { t } = useLocale();

type Props = {
  name: ModalTypes;
  organization: SlimOrganization;
};

const props = defineProps<Props>();

const { getRolesPossibleToCreateWith } = useUserRoles();
const availableRoles = ref<Role[]>([]);

watch(
  props.organization,
  async (newOrg) => {
    availableRoles.value = await getRolesPossibleToCreateWith(newOrg);
  },
  { immediate: true },
);

const emits = defineEmits<{
  (event: 'usersUploaded', value: UserToCreate[]): void;
}>();

onUpdated(() => {
  setTimeout(() => {
    textarea.value?.focus();
    // on Modal open fucus goes to close btn but not immediately, so delaying it
  }, 300);
});

const { isOpen, close } = useModal(props.name);

const textarea = ref();
const inputText = ref('');

const parseUsers = () => {
  const parsedUsers = [
    ...inputText.value
      .split('\n')
      .map((line) => {
        const [nameInput, roleInput, emailInput] = line.split(',').map((item) => item.trim().replace(/\s+/g, ' '));
        const role = roleInput?.toLowerCase() || '';
        const email = emailInput?.toLowerCase() || '';
        const nameParts = nameInput.split(' ');
        const firstName = nameParts.length === 1 ? nameParts[0] : nameParts.slice(0, -1).join(' ');
        const lastName = nameParts.length === 1 ? '' : nameParts.slice(-1).join(' ');

        return {
          key: generateRandomPassword(),
          name: nameInput,
          firstName,
          lastName,
          role: convertToRole(role),
          email: email || '',
          status: 'pending' as UserToCreate['status'],
          password: generateRandomPassword(),
          qrToken: generateQrToken(),
          errorMessages: '',
        };
      })
      .filter((user) => user.firstName), // Filter out empty lines
  ];

  emits('usersUploaded', parsedUsers);
  close();
};

const fileInput = ref<HTMLInputElement | null>(null);
const _getCSVFile = () => {
  if (fileInput.value) {
    fileInput.value.click();
  }
};
const handleFileUpload = (event: Event) => {
  const file = (event.target as HTMLInputElement).files?.[0];
  if (!file) return;

  const reader = new FileReader();
  reader.onload = (e) => {
    const text = e.target?.result as string;
    inputText.value +=
      '\n' +
      text
        .replace(/"(.*?)"/g, '$1') // Remove inner double quotes
        .replace(/'(.*?)'/g, '$1') // Remove inner single quotes
        .replace(/""/g, '') // Remove inner double quotes
        .replace(/''/g, ''); // Remove inner single quotes
  };
  reader.readAsText(file);
};

const convertToRole = (role: string): UserToCreate['role'] => {
  return (availableRoles.value.includes(role as Role) ? role : 'student') as UserToCreate['role'];
};
</script>

<template>
  <Modal
    :name="name"
    :title="t('lcp.upload_multiple_users')"
    :is-open="isOpen"
    :show-cancel-action="false"
    :action="parseUsers"
    :action-label="t('lcp.done')"
    containeda
    background-color="rgba(var(--v-theme-surface-container))"
    width="80%"
    max-width="1000px"
    :grow-contents="false"
    @afterLeave="inputText = ''"
  >
    <!--
  CSV Button should be hidden for now.  Use the props when to enable
    secondary-action-variant="tonal"
    secondary-action-disabled
     :secondary-action="getCSVFile"
    :secondary-action-label="t('lcp.upload_csv')"
    :secondary-action-prepend-icon="'mdi-tray-arrow-up'"
  -->

    <v-textarea
      ref="textarea"
      auto-focus
      v-model="inputText"
      :label="t('lcp.paste_users_names_here')"
      rows="8"
      variant="solo"
      flat
      block
      autocomplete="off"
      autocorrect="off"
      autocapitalize="off"
      spellcheck="false"
      hide-details
      style="height: 100%"
    ></v-textarea>
    <v-file-input ref="fileInput" style="display: none" @change="handleFileUpload" />
  </Modal>
</template>
